/** @flow */
import Add from '@mui/icons-material/Add';
import FilterList from '@mui/icons-material/FilterList';
import UnfoldLess from '@mui/icons-material/UnfoldLess';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import {
  TreeItemContent,
  TreeItemGroupTransition,
  TreeItemIcon,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemProvider,
  TreeItemRoot,
} from '@mui/x-tree-view';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { useTreeItem } from '@mui/x-tree-view/useTreeItem';
import SearchField from 'components/controls/SearchField';
import SectionTitle from 'components/@home/@settings/Tabs/SectionTitle';
import DeleteDialog from 'components/common/DeleteDialog';
import TeamForm from 'components/@home/drawers/TeamDrawer/Form';
import RulesForm from 'components/@home/drawers/RulesDrawer/RulesForm';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { debounce } from '@mui/material/utils';
import teamsActions from 'store/app/entities/teams/action';
import { useListTeamsQuery } from 'store/app/entities/TeamsSlice';
import { useListRulesQuery } from 'store/app/entities/TeamMembershipRulesSlice';
import teamDrawer from 'store/app/ui/drawers/team/action';
import { I18n } from 'utils/i18n';
import listFormat from 'utils/listFormat';

import listToTree from 'utils/listToTree';

const COMPACT_ROW_HEIGHT = 40;

const CustomTreeItemContent = styled(TreeItemContent)(({ theme }) => ({
  padding: theme.spacing(0.75, 1),
  position: 'relative',
  /*'&[data-nested]::before': {
    content: '""',
    position: 'absolute',
    left: -14,
    top: '50%',
    width: 28,
    height: 1,
    backgroundColor: theme.palette.divider,
  },*/
}));

const getStyles = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    minHeight: 0,
  },
  treePanel: {
    width: 250,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    minHeight: 0,
  },
  resizeHandle: {
    width: 9,
    flexShrink: 0,
    cursor: 'col-resize',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: '4px',
      width: '1px',
      backgroundColor: theme.palette.divider,
      transition: 'background-color 150ms',
    },
    '&::after': {
      content: '"···"',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%) rotate(90deg)',
      fontSize: 14,
      lineHeight: '8px',
      height: '9px',
      letterSpacing: 2,
      color: theme.palette.grey[600],
      fontWeight: 900,
      backgroundColor: theme.palette.grey[300],
      borderRadius: '8px',
      padding: '0 8px',
      userSelect: 'none',
      transition: 'background-color 150ms',
    },
    '&:hover::before, &:active::before': {
      backgroundColor: theme.palette.primary.main,
    },
    '&:hover::after, &:active::after': {
      backgroundColor: theme.palette.grey[400],
    },
  },
  treePanelHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  treePanelSearchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1),
    paddingRight: theme.spacing(0.5),
  },
  treeScroll: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  detailPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 300,
  },
  detailContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  iconContainer: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '50%',
    zIndex: 1,
  },
  treeItemLabel: {
    overflow: 'hidden',
    minWidth: 0,
    '& span': {
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  groupTransition: {
    marginLeft: '10px',
    paddingLeft: '10px',
    '& > div > div > li': {
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: '-5px',
        top: 0,
        bottom: 0,
        width: '1px',
        backgroundColor: theme.palette.divider,
      },
      '&:last-child::before': {
        height: 'calc(100% - 8px)',
      },
    },
  },
});

const filterTree = (nodes, query) => {
  if (!query) return nodes;
  const lowerQuery = query.toLowerCase();
  return nodes.reduce((acc, node) => {
    const filteredChildren = filterTree(node.children || [], query);
    const selfMatches = node.label.toLowerCase().includes(lowerQuery);
    if (selfMatches || filteredChildren.length > 0) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, []);
};

const getAllChildNames = team => {
  if (!team?.children) {
    return [];
  }
  return team.children.reduce((names, childTeam) => {
    return [...names, childTeam.name, ...getAllChildNames(childTeam)];
  }, []);
};

const CustomTreeItem = React.memo(
  React.forwardRef((props, ref) => {
    const {
      id,
      itemId,
      label,
      disabled,
      children,
      onSelectTeam,
      selectedTeamId,
      isFiltering,
      ...other
    } = props;
    const theme = useTheme();
    const styles = useMemo(() => getStyles(theme), [theme]);
    const labelTextRef = React.useRef(null);
    const [isLabelOverflowing, setIsLabelOverflowing] = React.useState(false);

    const updateOverflowState = useCallback(() => {
      const el = labelTextRef.current;
      if (!el) return;
      setIsLabelOverflowing(el.scrollWidth > el.clientWidth);
    }, []);

    React.useEffect(() => {
      const el = labelTextRef.current;
      if (!el) return undefined;

      const rafId = window.requestAnimationFrame(updateOverflowState);
      const observer = new ResizeObserver(updateOverflowState);
      observer.observe(el);
      if (el.parentElement) observer.observe(el.parentElement);

      return () => {
        window.cancelAnimationFrame(rafId);
        observer.disconnect();
      };
    }, [label, updateOverflowState]);

    const {
      getRootProps,
      getContentProps,
      getIconContainerProps,
      getLabelProps,
      getGroupTransitionProps,
      status,
      publicAPI,
    } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

    const hasBeenExpandedRef = React.useRef(false);
    if (status.expanded) hasBeenExpandedRef.current = true;

    const team = publicAPI.getItem(itemId);
    const hasChildren = team.children?.length > 0;
    const isSelected = selectedTeamId === itemId;

    const handleLabelClick = useCallback(
      e => {
        e.stopPropagation();
        onSelectTeam(team);
      },
      [onSelectTeam, team],
    );

    const bg = isSelected ? theme.palette.action.selected : theme.palette.background.paper;
    const contentStyle = {
      backgroundColor: bg,
    };
    const stickyRowStyle =
      hasChildren && !isFiltering
        ? {
            position: 'sticky',
            top: (team.depth || 0) * COMPACT_ROW_HEIGHT,
            zIndex: 10 - (team.depth || 0),
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 -4px 0 ${theme.palette.background.paper}, 0 4px 0 ${theme.palette.background.paper}`,
          }
        : {};

    return (
      <TreeItemProvider itemId={itemId}>
        <TreeItemRoot {...getRootProps(other)}>
          <Box sx={{ display: 'flex', flex: 1, minWidth: 0, ...stickyRowStyle }}>
            <CustomTreeItemContent
              {...getContentProps()}
              data-nested={team.depth > 0 ? '' : undefined}
              style={contentStyle}
            >
              <TreeItemIconContainer sx={styles.iconContainer} {...getIconContainerProps()}>
                <TreeItemIcon status={status} />
              </TreeItemIconContainer>
              <Tooltip
                title={isLabelOverflowing ? label : ''}
                placement="top-start"
                enterDelay={0}
                leaveDelay={0}
                disableInteractive
                disableHoverListener={!isLabelOverflowing}
                disableFocusListener={!isLabelOverflowing}
                disableTouchListener={!isLabelOverflowing}
                slotProps={{
                  tooltip: {
                    sx: {
                      m: 0,
                      py: 0.5,
                      px: 1,
                      whiteSpace: 'nowrap',
                      maxWidth: 'none',
                      width: 'max-content',
                      pointerEvents: 'none',
                    },
                  },
                  popper: {
                    sx: {
                      pointerEvents: 'none',
                      '& .MuiTooltip-tooltipPlacementTop, & .MuiTooltip-tooltipPlacementBottom, & .MuiTooltip-tooltipPlacementLeft, & .MuiTooltip-tooltipPlacementRight':
                        {
                          margin: '0 !important',
                        },
                    },
                    modifiers: [
                      { name: 'flip', enabled: false },
                      { name: 'preventOverflow', enabled: false },
                      {
                        name: 'positionOverReference',
                        enabled: true,
                        phase: 'write',
                        order: 9999,
                        fn: ({ state }) => {
                          const { x, y } = state.rects.reference;
                          const style = state.elements.popper.style;
                          style.left = `${x - 8}px`;
                          style.top = `${y - 4}px`;
                          style.transform = 'none';
                        },
                      },
                    ],
                  },
                }}
              >
                <TreeItemLabel
                  {...getLabelProps()}
                  onClick={handleLabelClick}
                  sx={styles.treeItemLabel}
                >
                  <Typography ref={labelTextRef} variant="body2" noWrap component="span">
                    {label}
                  </Typography>
                </TreeItemLabel>
              </Tooltip>
            </CustomTreeItemContent>
          </Box>
          {children && hasBeenExpandedRef.current && (
            <TreeItemGroupTransition {...getGroupTransitionProps()} sx={styles.groupTransition} />
          )}
        </TreeItemRoot>
      </TreeItemProvider>
    );
  }),
);

const getAllItemsWithChildrenItemIds = items => {
  const itemIds = [];
  const registerItemId = item => {
    if (item.children?.length) {
      itemIds.push(item.id);
      item.children.forEach(registerItemId);
    }
  };

  items.forEach(registerItemId);

  return itemIds;
};

const RulesTab = ({ team, onClose, onDelete }) => {
  const { data: rules = [], isLoading } = useListRulesQuery(team._id, {
    skip: !team?._id,
  });
  const form = useForm({ values: { rules } });

  return (
    <FormProvider {...form}>
      <RulesForm team={team} isLoading={isLoading} onClose={onClose} onDelete={onDelete} />
    </FormProvider>
  );
};

const TeamsSettings = () => {
  const locale = useSelector(state => state.i18n.locale);
  const [expandedItems, setExpandedItems] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [treePanelWidth, setTreePanelWidth] = useState(25);
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const dispatch = useDispatch();
  const rootRef = useRef(null);
  const isDraggingRef = useRef(false);

  const handleDragStart = useCallback(e => {
    e.preventDefault();
    isDraggingRef.current = true;
    const onMouseMove = moveEvent => {
      if (!isDraggingRef.current || !rootRef.current) return;
      const rootRect = rootRef.current.getBoundingClientRect();
      const pct = ((moveEvent.clientX - rootRect.left) / rootRect.width) * 100;
      setTreePanelWidth(Math.min(50, Math.max(15, pct)));
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const debouncedSetSearch = useMemo(() => debounce(val => setDebouncedSearch(val), 300), []);

  useEffect(() => () => debouncedSetSearch.clear(), [debouncedSetSearch]);

  const { data } = useListTeamsQuery({ page: null, perPage: 0, sort: null, sortBy: null });
  const teams = useMemo(() => listToTree(data?.teams), [data?.teams]);

  const filteredTeams = useMemo(() => filterTree(teams, debouncedSearch), [teams, debouncedSearch]);

  const searchExpandedItems = useMemo(
    () => (debouncedSearch ? getAllItemsWithChildrenItemIds(filteredTeams) : null),
    [debouncedSearch, filteredTeams],
  );

  const displayedExpandedItems = searchExpandedItems ?? expandedItems;

  const handleSelectTeam = useCallback(
    team => {
      setSelectedTeam(team);
      setActiveTab(0);
      dispatch(teamDrawer.populate(team));
    },
    [dispatch],
  );

  const addTeam = useCallback(() => {
    setSelectedTeam({});
    setActiveTab(0);
    dispatch(teamDrawer.populate());
  }, [dispatch]);

  const handleClosePanel = useCallback(() => {
    setSelectedTeam(null);
    setActiveTab(0);
  }, []);

  const handleDeleteTeam = useCallback(() => {
    setTeamToDelete(selectedTeam);
  }, [selectedTeam]);

  const confirmDelete = useCallback(() => {
    if (teamToDelete) {
      dispatch(teamsActions.destroy(teamToDelete._id));
    }
    setTeamToDelete(null);
    setSelectedTeam(null);
  }, [dispatch, teamToDelete]);

  const closeDialog = useCallback(() => {
    setTeamToDelete(null);
  }, []);

  const handleExpandedItemsChange = useCallback(
    (_, newExpanded) => {
      if (!debouncedSearch) {
        setExpandedItems(newExpanded);
      }
    },
    [debouncedSearch],
  );

  const handleExpandClick = useCallback(() => {
    setSearchInput('');
    setDebouncedSearch('');
    debouncedSetSearch.clear();
    if (displayedExpandedItems.length > 0) {
      setExpandedItems([]);
    } else {
      setExpandedItems(getAllItemsWithChildrenItemIds(teams));
    }
  }, [displayedExpandedItems.length, teams, debouncedSetSearch]);

  const handleSearchChange = useCallback(
    e => {
      setSearchInput(e.target.value);
      debouncedSetSearch(e.target.value);
    },
    [debouncedSetSearch],
  );

  const allChildNames = getAllChildNames(teamToDelete);

  return (
    <Box sx={styles.root} ref={rootRef}>
      <Box sx={styles.treePanel} style={{ width: `${treePanelWidth}%` }}>
        <Box sx={styles.treePanelHeader}>
          <SectionTitle title={I18n.t('SettingsMenu.teams')}>
            <Tooltip
              title={I18n.t(
                `TeamsSettings.${
                  displayedExpandedItems.length > 0 ? 'Collapse All' : 'Expand All'
                }`,
              )}
            >
              <IconButton size="small" onClick={handleExpandClick}>
                {displayedExpandedItems.length > 0 ? <UnfoldLess /> : <UnfoldMore />}
              </IconButton>
            </Tooltip>
          </SectionTitle>
          <Box sx={styles.treePanelSearchRow}>
            <SearchField
              value={searchInput}
              onChange={handleSearchChange}
              size="small"
              placeholder={I18n.t('TeamsSettings.Filter')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FilterList sx={{ fontSize: 20, color: 'secondary.main' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': { height: 38 },
              }}
            />
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={addTeam}
              startIcon={<Add />}
            >
              {I18n.t('TeamsSettings.New')}
            </Button>
          </Box>
        </Box>
        <Box sx={styles.treeScroll}>
          <RichTreeView
            items={filteredTeams}
            itemChildrenIndentation={20}
            slots={{ item: CustomTreeItem }}
            slotProps={{
              item: {
                onSelectTeam: handleSelectTeam,
                selectedTeamId: selectedTeam?._id,
                isFiltering: !!debouncedSearch,
              },
              root: {
                sx: { overflowY: 'auto', flex: 1, minHeight: 0 },
              },
            }}
            expandedItems={displayedExpandedItems}
            onExpandedItemsChange={handleExpandedItemsChange}
          />
        </Box>
      </Box>

      <Box sx={styles.resizeHandle} onMouseDown={handleDragStart} />

      {selectedTeam && (
        <Box sx={styles.detailPanel}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2 }}>
            <Typography variant="h6">
              {selectedTeam._id ? selectedTeam.name : I18n.t('TeamsSettings.New')}
            </Typography>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
              <Tab label={I18n.t('TeamsSettings.Details')} />
              {selectedTeam._id && <Tab label={I18n.t('TeamsSettings.Members')} />}
              {selectedTeam._id && <Tab label={I18n.t('TeamsSettings.Rules')} />}
            </Tabs>
          </Box>
          <Box sx={styles.detailContent}>
            {(activeTab === 0 || activeTab === 1) && (
              <TeamForm
                activeSection={activeTab === 0 ? 'details' : 'members'}
                onDelete={selectedTeam._id ? handleDeleteTeam : undefined}
                onClose={handleClosePanel}
              />
            )}
            {activeTab === 2 && selectedTeam._id && (
              <RulesTab
                team={selectedTeam}
                onClose={handleClosePanel}
                onDelete={handleDeleteTeam}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteDialog
        typeBack={teamToDelete?.name}
        message={I18n.t('TeamsSettings.ConfirmDelete', { name: teamToDelete?.name })}
        isOpen={teamToDelete}
        onConfirm={confirmDelete}
        onClose={closeDialog}
      >
        {teamToDelete?.children?.length ? (
          <Alert severity="warning">
            {I18n.t('TeamsSettings.WillDeleteChildren', {
              teams:
                allChildNames.length > 3
                  ? I18n.t('TeamsSettings.AndMore', {
                      names: allChildNames.slice(0, 3).join(', '),
                      count: allChildNames.length - 3,
                    })
                  : listFormat(locale, allChildNames),
            })}
          </Alert>
        ) : null}
      </DeleteDialog>
    </Box>
  );
};

export default TeamsSettings;
